/**
 * Utilidades para formateo de fechas en zona horaria de Chile
 * Maneja automáticamente horario de verano/invierno de Santiago
 */

// Configuración base para Chile
const CHILE_CONFIG = {
  timeZone: 'America/Santiago',
  locale: 'es-CL'
};

/**
 * Formateador genérico para fechas/horas de Chile
 * @param {Date|string} date - Fecha a formatear
 * @param {Object} options - Opciones de formateo
 * @returns {string} Fecha formateada o mensaje por defecto
 */
export const formatChileDateTime = (date, options = {}, defaultText = 'Sin fecha') => {
  if (!date) return defaultText;
  
  try {
    // Crear fecha y formatear directamente sin conversión de zona horaria
    // ya que las fechas del servidor suelen estar en UTC y queremos mostrarlas como están
    const dateObj = new Date(date);
    
    // Si las opciones incluyen timeZone, usarla; sino formatear la fecha tal como está
    if (options.timeZone) {
      return dateObj.toLocaleString(CHILE_CONFIG.locale, {
        timeZone: options.timeZone,
        ...options
      });
    } else {
      // Formatear sin conversión de zona horaria (usar la fecha tal como está)
      return dateObj.toLocaleString(CHILE_CONFIG.locale, options);
    }
  } catch (error) {
    console.error('Error formateando fecha:', error, date);
    return defaultText;
  }
};

/**
 * Formatear fecha y hora exactamente como el modal
 * Replica el comportamiento: new Date(date).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })
 * @param {Date|string} date - Fecha a formatear
 * @returns {Object} { fecha: '22/12/2025', hora: '14:30' }
 */
export const formatChileDateTimeModal = (date) => {
  if (!date) return { fecha: 'Sin fecha', hora: '-' };
  
  try {
    // EXACTAMENTE el mismo formateo que usa el modal
    const fullFormat = new Date(date).toLocaleString('es-CL', {
      dateStyle: 'short',
      timeStyle: 'short'
    });
    
    // Dividir por coma para obtener fecha y hora por separado
    const parts = fullFormat.split(',').map(part => part.trim());
    
    return {
      fecha: parts[0] || 'Sin fecha',
      hora: parts[1] || '-'
    };
  } catch (error) {
    console.error('Error formateando fecha:', error, date);
    return { fecha: 'Sin fecha', hora: '-' };
  }
};

/**
 * Formatear solo fecha usando método del modal
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Fecha en formato "22/12/2025"
 */
export const formatChileDate = (date) => {
  return formatChileDateTimeModal(date).fecha;
};

/**
 * Formatear solo hora usando método del modal
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Hora en formato que usa el modal
 */
export const formatChileTime = (date) => {
  return formatChileDateTimeModal(date).hora;
};

/**
 * Formatear hora completa con segundos (HH:MM:SS)
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Hora en formato "14:30:25"
 */
export const formatChileTimeWithSeconds = (date) => {
  return formatChileDateTime(date, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }, '-');
};

/**
 * Formatear fecha y hora completa
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Fecha y hora en formato "22/12/2025, 14:30:25"
 */
export const formatChileFullDateTime = (date) => {
  return formatChileDateTime(date, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

/**
 * Formatear fecha y hora sin segundos
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Fecha y hora en formato "22/12/2025, 14:30"
 */
export const formatChileDateTime12 = (date) => {
  return formatChileDateTime(date, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/**
 * Formatear fecha relativa (ayer, hoy, mañana)
 * @param {Date|string} date - Fecha a formatear
 * @returns {string} Descripción relativa o fecha formateada
 */
export const formatChileRelativeDate = (date) => {
  if (!date) return 'Sin fecha';
  
  const inputDate = new Date(date);
  const today = new Date();
  
  // Normalizar fechas a medianoche para comparación
  const normalizeDate = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const normalizedInput = normalizeDate(inputDate);
  const normalizedToday = normalizeDate(today);
  
  const diffTime = normalizedInput.getTime() - normalizedToday.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Hoy';
  if (diffDays === -1) return 'Ayer';
  if (diffDays === 1) return 'Mañana';
  if (diffDays > 1 && diffDays <= 7) return `En ${diffDays} días`;
  if (diffDays < -1 && diffDays >= -7) return `Hace ${Math.abs(diffDays)} días`;
  
  // Para fechas más lejanas, mostrar fecha formateada
  return formatChileDate(date);
};

/**
 * Obtener timestamp actual de Chile (CON conversión de zona horaria)
 * @returns {string} Timestamp formateado en hora de Chile
 */
export const getChileNow = () => {
  return formatChileDateTime(new Date(), {
    timeZone: CHILE_CONFIG.timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

/**
 * Obtener solo la fecha actual de Chile (CON conversión de zona horaria)
 * @returns {string} Fecha actual formateada en hora de Chile
 */
export const getChileToday = () => {
  return formatChileDateTime(new Date(), {
    timeZone: CHILE_CONFIG.timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Obtener fecha actual en formato ISO para inputs de fecha (YYYY-MM-DD)
 * @returns {string} Fecha en formato ISO
 */
export const getChileTodayISO = () => {
  const now = new Date();
  // Convertir a hora de Chile primero
  const chileDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/Santiago' }));
  return chileDate.toISOString().split('T')[0]; // YYYY-MM-DD
};

/**
 * Validar si una fecha es válida
 * @param {Date|string} date - Fecha a validar
 * @returns {boolean} True si es fecha válida
 */
export const isValidDate = (date) => {
  if (!date) return false;
  const d = new Date(date);
  return d instanceof Date && !isNaN(d.getTime());
};

export default {
  formatChileDateTime,
  formatChileDate,
  formatChileTime,
  formatChileTimeWithSeconds,
  formatChileFullDateTime,
  formatChileDateTime12,
  formatChileRelativeDate,
  getChileNow,
  getChileToday,
  getChileTodayISO,
  isValidDate
};